import {Link} from 'react-router'
import {getCrossProductLinks} from '@core/products/cross-links'
import {homeFor, HREFLANG_MAP} from '@product/quies/lib/hreflang'
import {HOME_LANGS} from '@product/quies/lib/homeLangs'
import {languageName} from '@product/quies/data/languages'
import {useInterfaceStrings} from '@product/quies/i18n/catalogue'
import type {InterfaceStrings} from '@product/quies/i18n/strings'
import {LOCAL_PAGES} from '@product/quies/data/local-pages/registry'
import styles from './Footer.module.scss'

const siblingProducts = getCrossProductLinks('quies')

/** The homes, each named in its own language. */
const HOME_LINKS = HOME_LANGS.map((lang) => ({label: languageName(lang), lang}))

type FooterColumn = {heading: string; links: {label: string; to: string; lang?: string}[]}

/** Every local page on every page, each named in its own language. */
const METHOD_LINKS = Object.values(LOCAL_PAGES).map(({anchor, path, lang}) => ({label: anchor, to: path, lang}))

function buildColumns(lang: string, {nav, footer: l}: InterfaceStrings): FooterColumn[] {
    const home = homeFor(lang)

    const resolve = (topic: string) => HREFLANG_MAP[topic]?.[lang] ?? HREFLANG_MAP[topic]?.en ?? '/'

    return [
        {
            heading: 'Quies',
            links: [
                {label: nav.beginSession, to: '/session'},
                {label: nav.pricing, to: `${home}#pricing`},
                {label: nav.faq, to: `${home}#faq`},
            ],
        },
        {
            heading: l.selfHypnosisFor,
            links: [
                {label: l.anxiety, to: resolve('anxiety')},
                {label: l.sleep, to: resolve('sleep')},
                {label: l.stress, to: resolve('stress')},
                {label: l.confidence, to: resolve('confidence')},
                {label: l.focus, to: resolve('focus')},
            ],
        },
        {
            heading: l.learn,
            links: [
                {label: l.ericksonian, to: resolve('ericksonian')},
                {label: l.aiHow, to: resolve('ai-how-it-works')},
                {label: l.vsMeditation, to: resolve('vs-meditation')},
            ],
        },
        {heading: l.methods, links: METHOD_LINKS},
        {
            heading: l.languages,
            links: HOME_LINKS.map(({label, lang: target}) => ({label, to: homeFor(target), lang: target})),
        },
    ]
}

export const Footer = () => {
    const {lang, t} = useInterfaceStrings()
    const columns = buildColumns(lang, t)

    return (
        <footer className={styles.footer}>
            <div className={styles.footerColumns}>
                {columns.map((col) => (
                    <div key={col.heading} className={styles.footerColumn}>
                        <h4 className={styles.columnHeading}>{col.heading}</h4>
                        <ul className={styles.columnList}>
                            {col.links.map((link) => (
                                <li key={link.to}>
                                    <Link to={link.to} lang={link.lang} className={styles.footerLink}>
                                        {link.label}
                                    </Link>
                                </li>
                            ))}
                        </ul>
                    </div>
                ))}
            </div>
            <div className={styles.crossLinks}>
                {siblingProducts.map((p) => (
                    <a
                        key={p.id}
                        href={p.url}
                        target="_blank"
                        rel="noopener"
                        className={styles.crossLink}
                    >
                        {p.name}
                        <span className={styles.crossLinkTagline}>{p.tagline}</span>
                    </a>
                ))}
            </div>
            <div className={styles.footerBottom}>
                <span className={styles.footerContact}>contact@quies.ai</span>
                <Link to="/legal" className={styles.footerLink}>
                    {t.nav.legal}
                </Link>
            </div>
        </footer>
    )
}
